import React, { ReactNode, Ref, forwardRef } from 'react';
import { classNames } from '../../utils';
import { TextInput } from './text-input';
import {
  NumberFormatValues,
  NumericFormat,
  OnValueChange,
  SourceInfo,
} from 'react-number-format';
import { FieldError, useFormContext } from 'react-hook-form';

interface NumberInputProps {
  className?: string;
  type?: React.HTMLInputTypeAttribute;
  name?: string;
  placeholder?: string;
  leftIcon?: ReactNode;
  value?: number | null;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onValueChange?: (values: NumberFormatValues, sourceInfo: SourceInfo) => void;
  onChange?: (val?: number) => void;
  onSubmit?: (value: number | string | null) => void;
  disabled?: boolean;
  rightIcon?: ReactNode;
  fieldErrorOverride?: FieldError;
  autoFocus?: boolean;
  inputClassName?: string;
  allowNegative?: boolean;
  maxLength?: number;
  min?: number;
  max?: number;
  allowLeadingZeros?: boolean;
  submitOnEnter?: boolean;
  showBorder?: boolean;
}

// eslint-disable-next-line
export const NumberInput: React.FC<NumberInputProps> = (props) => {
  const {
    className,
    type,
    name,
    value,
    disabled,
    placeholder,
    leftIcon,
    onBlur,
    onValueChange,
    onChange,
    fieldErrorOverride,
    autoFocus,
    inputClassName = '',
    allowNegative,
    maxLength,
    min,
    max,
    allowLeadingZeros,
    submitOnEnter,
    onSubmit,
    showBorder = true,
  } = props;

  const currentValue =
    typeof value === 'undefined' || value === null || value === undefined
      ? ''
      : value;

  return (
    <NumericFormat
      disabled={disabled}
      value={currentValue}
      onValueChange={(values, sourceInfo) => {
        sourceInfo?.event?.preventDefault();
        onValueChange?.(values, sourceInfo);
        onChange?.(values.floatValue);
      }}
      type="text"
      allowLeadingZeros={allowLeadingZeros}
      thousandSeparator=""
      customInput={TextInput}
      leftIcon={null}
      rightIcon={props.rightIcon}
      name={name}
      onBlur={onBlur}
      placeholder={placeholder}
      className={className}
      fieldErrorOverride={fieldErrorOverride}
      autoFocus={autoFocus}
      inputClassName={inputClassName}
      allowNegative={allowNegative}
      showBorder={showBorder}
      onKeyDown={(e: { key: string; preventDefault: () => any }) => {
        e.key === 'Enter' && e.preventDefault();
      }}
      maxLength={maxLength}
      min={min}
      isAllowed={(values) => {
        const { floatValue } = values;

        if (floatValue == null) return true;

        if (min != null && floatValue < min) {
          return false;
        }

        if (max != null && floatValue > max) {
          return false;
        }

        return true;
      }}
      submitOnEnter={submitOnEnter}
      onSubmit={onSubmit}
    />
  );
};
